import React, { useState } from "react";
import { FrontPageSectionProps } from "../../pages";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import Stars from "./stars";
import { useOptions } from "../../hooks/useOptions";
import { Product } from "../../utils/types";
import { getPrices, printPrice } from "../../utils/utils";
import { __t } from "../../utils/translate";

const ProductCard: React.FC<FrontPageSectionProps> = ({ product, className, loading = 'lazy' }) => {

    const href = new URL(product.permalink).pathname;
    const options = useOptions();
    const currency_symbol = options.wpOptions.data.currency_symbol;

    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };
    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    const prices = getPrices(product);

    return (
        <Link to={href} data-cy="product_card" className="w-full">
            <div className={`group flex flex-col bg-white shadow-card
                        p-[4px] sm:p-1 pb-3 sm:pb-[12px] md:pb-2 md:py-4 lg:p-[15px] w-full h-full
                        ${className} `}>
                <div className="relative w-full">
                    <div className="w-full stroke-black bg-light-blue overflow-hidden group-hover:scale-[0.97] transition duration-300">
                        <MainImage
                            product={product}
                            isHovered={isHovered}
                            loading={loading}
                        />
                    </div>
                </div>

                <p className={`text-base-16 text-center pt-5 text-wrap mb-auto md:min-h-[62px] leading-[21px]`} dangerouslySetInnerHTML={{ __html: product.name }} />
                {product.stock_status === 'instock' && (

                    prices?.min_sale_price === prices?.max_sale_price && (

                        !!prices?.sale_price && (
                            <div className="!font-serif w-full overflow-visible flex flex-col-reverse md:flex-row justify-center lg:gap-[5%] pb-1 md:pb-2 items-center mt-6 md:mt-[40px] text-18 md:text-[21px] md:leading-[21px] text-center !font-semibold leading-[18px]">
                                <span className={`product_card_sale_price text-green w-full`}>
                                    <div className="inline xl:whitespace-nowrap"
                                        dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                    ></div>
                                    {prices?.sale_price}
                                </span>
                                <span className={`product_card_regular_price line-through text-red w-full`}>
                                    <div className="inline xl:whitespace-nowrap"
                                        dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                    ></div>
                                    {prices?.regular_price}
                                </span>
                            </div>
                        ) || (
                            <span className={`product_card_price !font-serif text-18 text-center text-green !font-semibold w-full`}>
                                <div className="inline xl:whitespace-nowrap"
                                    dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                ></div>
                                {prices?.regular_price}
                            </span>
                        )
                    ) || (
                        <div className="w-full product_card_price !font-serif overflow-visible text-center flex flex-col md:flex-row justify-center items-center pb-1 md:pb-2 mt-6 text-18 md:text-[21px] font-semibold text-green leading-[18px]">
                            <span className={`lg:w-full xl:w-fit lg:whitespace-nowrap`}>
                                <div className="inline"
                                    dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                ></div>
                                {prices?.min_sale_price}
                            </span>
                            <span className="inline"> – </span>
                            <span className={`ml-1 lg:w-full xl:w-fit lg:whitespace-nowrap`}>
                                <div className="inline "
                                    dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                ></div>
                                {prices?.max_sale_price}
                            </span>
                        </div>
                    )
                ) || product.stock_status === 'discontinued' && (
                    <p className={`text-base-16-strong md:text-title text-center mt-[12px] text-red`}>
                        {__t('This product has been discontinued.')}
                    </p>
                ) || (
                        <p className={`text-base-16-strong md:text-title text-center mt-[12px] text-red`}>
                            {__t('Out of stock')}
                        </p>
                    )}


            </div>
        </Link>
    );
};

const getImageDataLocalFile = (localFile) => {
    if (!localFile) {
        return null;
    }
    const type = localFile.internal?.mediaType?.split("/")[0];

    if (type === 'video') {
        return null;
    }

    // return localFile.childImageSharp?.gatsbyImageData;

    if (!localFile.childrenImageSharp?.length) {
        return;
    }
    return localFile.childrenImageSharp[0].gatsbyImageData;
}

type ImageProps = {
    product: Product;
    isHovered: boolean;
    mainImageLoading?: "eager" | "lazy";
    loading?: "eager" | "lazy";
};

const MainImage: React.FC<ImageProps> = ({ product, isHovered, mainImageLoading, loading = 'lazy' }) => {
    const image = getImageDataLocalFile(product.image.localFile);

    if (image) {
        return (
            <GatsbyImage
                image={image}
                className={`${isHovered ? 'scale-105' : ''} aspect-square w-full block object-contain transition-all duration-500 overflow-hidden`}
                alt={product.image.alt}
                loading={mainImageLoading || loading}
                imgClassName="aspect-square w-full max-w-[490px]"
            />
        );
    } else
        return (
            <img
                className="!absolute left-0 top-0 w-max object-contain opacity-0 transition-all duration-500 hover:opacity-100"
                src={product.image.src}
                alt={product.image.alt}
                loading={loading}
            />
        );
};

export default ProductCard;
